import { useAuthStore } from '@/stores/auth'
import { authService } from '@/services/auth'
import { tokenManager } from '@/services/tokenManager'

/**
 * Session Manager
 * 
 * Manages user session and automatic token refresh.
 * - Monitors token expiration every 60 seconds
 * - Automatically refreshes tokens expiring in < 5 minutes
 * - Prevents concurrent refresh operations
 * - Handles refresh failures by clearing auth and redirecting to login
 */
class SessionManager {
  private monitoringInterval: number | null = null
  private refreshInProgress: boolean = false
  private refreshPromise: Promise<void> | null = null

  /**
   * Start monitoring token expiration
   * 
   * Checks token expiry every 60 seconds and refreshes if needed
   */
  startMonitoring(): void {
    // Clear any existing interval
    this.stopMonitoring()

    // Check immediately
    this.checkAndRefresh()

    // Set up interval to check every 60 seconds
    this.monitoringInterval = window.setInterval(() => {
      this.checkAndRefresh()
    }, 60 * 1000) // 60 seconds
  }

  /**
   * Stop monitoring token expiration
   */
  stopMonitoring(): void {
    if (this.monitoringInterval !== null) {
      clearInterval(this.monitoringInterval)
      this.monitoringInterval = null
    }
  }

  /**
   * Check token expiration and refresh if needed
   * 
   * Refreshes tokens that are expiring in < 5 minutes
   * Prevents concurrent refresh operations
   */
  async checkAndRefresh(): Promise<void> {
    const authStore = useAuthStore()

    // Skip if not authenticated
    if (!authStore.isAuthenticated) {
      return
    }

    // Skip if token is not expiring soon
    if (!authStore.isTokenExpiringSoon) {
      return
    }

    // If refresh is already in progress, wait for it
    if (this.refreshInProgress && this.refreshPromise) {
      await this.refreshPromise
      return
    }

    // Start refresh operation
    this.refreshInProgress = true
    this.refreshPromise = this.performRefresh()

    try {
      await this.refreshPromise
    } finally {
      this.refreshInProgress = false
      this.refreshPromise = null
    }
  }

  /**
   * Perform token refresh operation
   */
  private async performRefresh(): Promise<void> {
    const authStore = useAuthStore()

    try {
      // Get refresh token
      const refreshToken = tokenManager.getRefreshToken()

      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      console.log('Refreshing access token...')

      // Call Cognito to refresh token
      const tokens = await authService.refreshToken(refreshToken)

      // Store new tokens
      await tokenManager.storeTokens({
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        idToken: tokens.id_token,
      })

      // Update auth store
      authStore.setTokens({
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        idToken: tokens.id_token,
      })

      console.log('Access token refreshed successfully')
    } catch (error) {
      console.error('Token refresh failed:', error)

      // Clear auth state
      authStore.clearAuth()
      tokenManager.clearTokens()

      // Stop monitoring
      this.stopMonitoring()

      // Redirect to login
      // Note: In a real app, you might want to use router.push instead
      // But we don't have access to router here, so we'll use window.location
      window.location.href = '/login'
    }
  }

  /**
   * Force refresh token immediately
   * 
   * @returns Promise that resolves when refresh is complete
   */
  async forceRefresh(): Promise<void> {
    // If refresh is already in progress, wait for it
    if (this.refreshInProgress && this.refreshPromise) {
      await this.refreshPromise
      return
    }

    // Start refresh operation
    this.refreshInProgress = true
    this.refreshPromise = this.performRefresh()

    try {
      await this.refreshPromise
    } finally {
      this.refreshInProgress = false
      this.refreshPromise = null
    }
  }

  /**
   * Check if refresh is currently in progress
   */
  isRefreshing(): boolean {
    return this.refreshInProgress
  }
}

// Export singleton instance
export const sessionManager = new SessionManager()
